import * as styles from "./ProjectEditorBody.css";

import { useAtom, useAtomValue } from "jotai";
import type { JSX, ReactNode } from "react";

import type { IconName } from "@frontend/components/atoms/Icon";
import { Spinner } from "@frontend/components/atoms/Spinner";
import {
  Tabs,
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsPanel,
  TabsTab,
} from "@frontend/components/atoms/Tabs";
import { Trans, useLingui } from "@lingui/react/macro";

import { editorTabAtom, projectEditorAtom } from "../stores/editor";
import { BalanceSheetPanel } from "./BalanceSheetPanel";
import { BillingTab } from "./BillingTab";
import { ClosureWeeksPanel } from "./ClosureWeeksPanel";
import { ComplementaryTab } from "./ComplementaryTab";
import { ContractList } from "./ContractList";
import { InterventionList } from "./InterventionList";
import { JobList } from "./JobList";
import { MainInformations } from "./MainInformations";
import { NotesTab } from "./NotesTab";

export type ProjectEditorBodyProps = {
  loading: boolean;
  error: string | null;
  conflict: boolean;
  stickyTabs?: boolean;
};

type Tab = {
  value: string;
  icon: IconName;
  label: ReactNode;
  panel: ReactNode;
};

export function ProjectEditorBody({
  loading,
  error,
  conflict,
  stickyTabs = false,
}: ProjectEditorBodyProps): JSX.Element {
  const { t } = useLingui();

  const { type, project } = useAtomValue(projectEditorAtom);
  const [tab, setTab] = useAtom(editorTabAtom);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner aria-label={t`Loading`} />
      </div>
    );
  }

  const tabs = tabsFor(type, project);

  return (
    <div className={styles.body}>
      {error ? (
        <p className={styles.alert} role="alert">
          {conflict ? (
            <Trans>
              Someone else saved this project while it was open. Reopen it to
              see their version, then make your change again.
            </Trans>
          ) : (
            error
          )}
        </p>
      ) : null}

      <Tabs
        value={tab}
        onValueChange={setTab}
        variant="underline"
        className={styles.tabs}
      >
        <TabsList sticky={stickyTabs}>
          <TabsTab value="main" icon="Info">
            <Trans>Main information</Trans>
          </TabsTab>

          <TabsTab value="notes" icon="MessageSquare">
            <Trans>Notes</Trans>
          </TabsTab>

          <TabsTab value="billing" icon="ReceiptText">
            <Trans>Billing</Trans>
          </TabsTab>

          {tabs.map((tab) => (
            <TabsTab key={tab.value} value={tab.value} icon={tab.icon}>
              {tab.label}
            </TabsTab>
          ))}

          <TabsIndicator />
        </TabsList>

        <TabsContent className={styles.tabsContent}>
          <TabsPanel value="main">
            <MainInformations />
          </TabsPanel>

          <TabsPanel value="notes" className={styles.fillPanel}>
            <NotesTab />
          </TabsPanel>

          <TabsPanel value="billing" className={styles.fillPanel}>
            <BillingTab />
          </TabsPanel>

          {tabs.map((tab) => (
            <TabsPanel key={tab.value} value={tab.value}>
              {tab.panel}
            </TabsPanel>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function tabsFor(
  type: "subscription" | "intervention",
  project: {
    id: number;
    intervention?: { billable: boolean } | null;
    subscription?: {
      closureConcerned: boolean;
      closurePlannable: boolean;
    } | null;
  } | null,
): Tab[] {
  if ("intervention" === type) {
    const tabs: Tab[] = [
      {
        value: "complementary",
        icon: "CalendarClock",
        label: <Trans>Dates and estimates</Trans>,
        panel: <ComplementaryTab />,
      },
    ];

    if (null !== project && true === project.intervention?.billable) {
      tabs.push({
        value: "balance",
        icon: "Scale",
        label: <Trans>Balance sheet and billing</Trans>,
        panel: <BalanceSheetPanel projectId={project.id} />,
      });
    }

    return tabs;
  }

  if (null === project) {
    return [];
  }

  const tabs: Tab[] = [
    {
      value: "jobs",
      icon: "Users",
      label: <Trans>Work posts</Trans>,
      panel: <JobList projectId={project.id} />,
    },
    {
      value: "contracts",
      icon: "FileSignature",
      label: <Trans>Contract</Trans>,
      panel: <ContractList projectId={project.id} />,
    },
  ];

  const subscription = project.subscription ?? null;

  if (
    null !== subscription &&
    (subscription.closureConcerned || subscription.closurePlannable)
  ) {
    tabs.push({
      value: "closure-weeks",
      icon: "CalendarOff",
      label: <Trans>Closure weeks</Trans>,
      panel: (
        <ClosureWeeksPanel
          projectId={project.id}
          readOnly={!subscription.closurePlannable}
        />
      ),
    });
  }

  tabs.push({
    value: "interventions",
    icon: "Zap",
    label: <Trans>Interventions</Trans>,
    panel: <InterventionList projectId={project.id} />,
  });

  return tabs;
}
